/**
 * Apply Coupon functionality for Wellbeing Nutrition
 * Implements the coupon application using Shopify's cart/update.js API
 */

class ApplyCoupon {
  constructor() {
    this.isLoading = false;
    this.apiUrl = "/cart/update.js";
  }

  /**
   * Apply a discount code to the cart
   * @param {string} discountCode - The discount code to apply
   * @returns {Promise<Object>} - The response from the API
   */
  async applyDiscount(discountCode) {
    if (this.isLoading) {
      return {
        success: false,
        message: "Please wait, processing previous request...",
      };
    }

    if (!discountCode || !discountCode.trim()) {
      return { success: false, message: "Please enter a coupon code" };
    }

    // Check if a coupon is already applied
    const appliedCode = sessionStorage.getItem("appliedCouponCode");
    if (appliedCode && appliedCode !== discountCode.trim()) {
      return {
        success: false,
        message: `Please remove the current coupon (${appliedCode}) before applying a new one.`,
      };
    }

    this.isLoading = true;

    try {
      // Prepare form data
      const formData = new URLSearchParams();
      formData.append("discount", discountCode.trim());

      // Make the API call using fetch (equivalent to the curl command)
      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json, text/javascript, */*; q=0.01",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData.toString(),
      });

      if (!response.ok) {
        throw new Error(`Failed to apply coupon. Status: ${response.status}`);
      }

      const data = await response.json();

      // Check if the discount code was successfully applied
      // When a discount code is invalid or not applicable, Shopify's cart/update.js API
      // either returns an error status or doesn't include any discount applications
      if (response.status === 422) {
        // 422 Unprocessable Entity typically indicates invalid discount code
        document.dispatchEvent(new CustomEvent("coupon:invalid", {
          detail: {
            couponCode: discountCode.trim(),
          },
        }));
        return {
          success: false,
          message: "Invalid Discount Code. Please enter a valid discount code",
        };
      }

      // Check if discount was actually applied by looking for cart_level_discount_applications
      const hasDiscountApplications =
        data.cart_level_discount_applications &&
        data.cart_level_discount_applications.length > 0;

      // If we tried to apply a discount but no discount applications exist, the code was invalid
      if (!hasDiscountApplications) {
        document.dispatchEvent(new CustomEvent("coupon:invalid", {
          detail: {
            couponCode: discountCode.trim(),
          },
        }));
        return {
          success: false,
          message: "Invalid Discount Code. Please enter a valid discount code",
        };
      }

      // Update cart UI and trigger cart updated event
      this.handleSuccessfulApplication(discountCode, data);
      const savingsAmount = data.cart_level_discount_applications[0].amount;
      document.dispatchEvent(new CustomEvent("coupon:applied", {
        detail: {
          couponCode: discountCode,
          discountValue: savingsAmount/100,
        },
      }));
      return {
        success: true,
        message: `Coupon ${discountCode} applied successfully!`,
        data: data,
      };
    } catch (error) {
      console.error("Error applying coupon:", error);
      this.handleError(error);

      return {
        success: false,
        message: error.message || "Failed to apply coupon. Please try again.",
      };
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle successful coupon application
   * @param {string} discountCode - The applied discount code
   * @param {Object} cartData - Updated cart data from API
   */
  handleSuccessfulApplication(discountCode, cartData) {
    // Calculate savings amount from cart data using original_total_price - total_price
    let savingsAmount = 0;

    if (cartData.original_total_price && cartData.total_price) {
      savingsAmount = cartData.original_total_price - cartData.total_price;
    } else {
      // Fallback: Try to get from cart_level_discount_applications if prices are not available
      if (
        cartData.cart_level_discount_applications &&
        cartData.cart_level_discount_applications.length > 0
      ) {
        // Find the discount that matches our applied code
        const appliedDiscount = cartData.cart_level_discount_applications.find(
          (discount) =>
            discount.title &&
            discount.title.toUpperCase().includes(discountCode.toUpperCase())
        );

        if (appliedDiscount) {
          savingsAmount = appliedDiscount.total_allocated_amount;
        } else {
          // If we can't find the specific discount, use the total of all discounts
          savingsAmount = cartData.cart_level_discount_applications.reduce(
            (total, discount) => total + (discount.total_allocated_amount || 0),
            0
          );
        }
      }
    }

    // Trigger cart updated event
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: {
          cart: cartData,
          appliedDiscount: discountCode,
        },
      })
    );

    // Update cart drawer if it exists
    if (typeof updateCartCount === "function") {
      updateCartCount();
    }

    // Refresh cart drawer content
    if (window.theme && window.theme.CartDrawer) {
      const cartDrawer = new window.theme.CartDrawer();
      if (cartDrawer.buildCart) {
        cartDrawer.buildCart();
      }
    }

    // Update apply button states to show applied design with savings
    this.updateApplyButtonStates(discountCode, savingsAmount);

    // Show coupon success popup
    if (typeof window.showCouponPopup === "function") {
      window.showCouponPopup(discountCode, savingsAmount);
    }
  }

  /**
   * Update apply button states to show applied design
   * @param {string} discountCode - The applied discount code
   * @param {number} savingsAmount - The savings amount in cents
   */
  updateApplyButtonStates(discountCode, savingsAmount = 0) {
    // Store the applied coupon code and savings
    sessionStorage.setItem("appliedCouponCode", discountCode);
    sessionStorage.setItem("appliedCouponSavings", savingsAmount.toString());

    // Update main cart input section
    const mainInput = document.getElementById("coupon-main-input");
    const mainApplied = document.getElementById("coupon-main-applied");
    const mainCode = document.getElementById("coupon-main-code");
    const mainSavings = document.getElementById("coupon-main-savings");

    if (mainInput && mainApplied && mainCode && mainSavings) {
      // Hide input, show applied state
      mainInput.classList.add("hidden");
      mainApplied.classList.remove("hidden");

      // Update applied state content
      mainCode.textContent = discountCode;
      mainSavings.textContent = `Saved ₹${(savingsAmount / 100).toFixed(2)}`;
    }

    // Update coupon drawer input section
    const drawerInput = document.getElementById("coupon-drawer-input");
    const drawerApplied = document.getElementById("coupon-drawer-applied");
    const drawerCode = document.getElementById("coupon-drawer-code");
    const drawerSavings = document.getElementById("coupon-drawer-savings");

    if (drawerInput && drawerApplied && drawerCode && drawerSavings) {
      // Hide input, show applied state
      drawerInput.classList.add("hidden");
      drawerApplied.classList.remove("hidden");

      // Update applied state content
      drawerCode.textContent = discountCode;
      drawerSavings.textContent = `Saved ₹${(savingsAmount / 100).toFixed(2)}`;
    }
  }

  /**
   * Reset apply button states (call when coupon is removed)
   */
  resetApplyButtonStates() {
    // Clear stored coupon code and savings
    sessionStorage.removeItem("appliedCouponCode");
    sessionStorage.removeItem("appliedCouponSavings");

    // Reset main cart input section
    const mainInput = document.getElementById("coupon-main-input");
    const mainApplied = document.getElementById("coupon-main-applied");
    const mainInputField = document.getElementById("couponCodeInputMain");

    if (mainInput && mainApplied) {
      // Show input, hide applied state
      mainInput.classList.remove("hidden");
      mainApplied.classList.add("hidden");
    }

    // Clear input field
    if (mainInputField) {
      mainInputField.value = "";
    }

    // Reset coupon drawer input section
    const drawerInput = document.getElementById("coupon-drawer-input");
    const drawerApplied = document.getElementById("coupon-drawer-applied");
    const drawerInputField = document.getElementById("couponCodeInput");

    if (drawerInput && drawerApplied) {
      // Show input, hide applied state
      drawerInput.classList.remove("hidden");
      drawerApplied.classList.add("hidden");
    }

    // Clear input field
    if (drawerInputField) {
      drawerInputField.value = "";
    }
  }

  /**
   * Check and restore applied state on page load
   */
  checkAppliedState() {
    const appliedCode = sessionStorage.getItem("appliedCouponCode");
    const appliedSavings = sessionStorage.getItem("appliedCouponSavings");

    if (appliedCode) {
      const savingsAmount = appliedSavings ? parseInt(appliedSavings) : 0;
      this.updateApplyButtonStates(appliedCode, savingsAmount);
    }
  }

  /**
   * Remove applied discount
   * @returns {Promise<Object>} - The response from the operation
   */
  async removeDiscount() {
    if (this.isLoading) {
      return {
        success: false,
        message: "Please wait, processing previous request...",
      };
    }

    this.isLoading = true;

    try {
      const appliedCode = sessionStorage.getItem("appliedCouponCode");
      const appliedSavings = sessionStorage.getItem("appliedCouponSavings");
      const savingsAmount = appliedSavings ? parseInt(appliedSavings) : 0;

      // Make the API call to remove discount using update.js with empty discount
      const response = await fetch(this.apiUrl, {
        method: "POST",
        headers: {
          Accept: "application/json, text/javascript, */*; q=0.01",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: "discount=", // Empty discount removes all discounts
      });

      if (!response.ok) {
        throw new Error(`Failed to remove coupon. Status: ${response.status}`);
      }

      const data = await response.json();

      // Reset apply button states
      this.resetApplyButtonStates();

      // Trigger cart updated event
      document.dispatchEvent(
        new CustomEvent("cart:updated", {
          detail: {
            cart: data,
          },
        })
      );

      // Update cart drawer if it exists
      if (typeof updateCartCount === "function") {
        updateCartCount();
      }

      // Refresh cart drawer content
      if (window.theme && window.theme.CartDrawer) {
        const cartDrawer = new window.theme.CartDrawer();
        if (cartDrawer.buildCart) {
          cartDrawer.buildCart();
        }
      }

      const newCartValue = data.total_price;
      document.dispatchEvent(new CustomEvent("coupon:removed", {
        detail: {
          couponCode: appliedCode,
          discountValue: savingsAmount/100,
          newCartValue: newCartValue/100,
        },
      }));

      return {
        success: true,
        message: "Coupon removed successfully!",
        data: data,
      };
    } catch (error) {
      console.error("Error removing coupon:", error);
      this.handleError(error);

      return {
        success: false,
        message: error.message || "Failed to remove coupon. Please try again.",
      };
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle errors during coupon application
   * @param {Error} error - The error object
   */
  handleError(error) {
    // Show error notification
    this.showErrorNotification(error.message);
  }

  /**
   * Show success notification
   * @param {string} discountCode - The applied discount code
   */
  showSuccessNotification(discountCode) {
    // Try to use existing notification system
    const notificationElement = document.getElementById("notifyATC");
    if (notificationElement) {
      const messageEl = notificationElement.querySelector(
        ".notification-message"
      );
      if (messageEl) {
        messageEl.textContent = `Coupon ${discountCode} applied successfully!`;
      }
      notificationElement.classList.add("show");
      setTimeout(() => {
        notificationElement.classList.remove("show");
      }, 3000);
    } else {
      // Fallback to alert if no notification system
      alert(`Coupon ${discountCode} applied successfully!`);
    }
  }

  /**
   * Show error notification
   * @param {string} message - The error message
   */
  showErrorNotification(message) {
    // Create or update error display
    let errorEl = document.getElementById("coupon-error-message");
    if (!errorEl) {
      errorEl = document.createElement("div");
      errorEl.id = "coupon-error-message";
      errorEl.style.cssText = `        background-color: #fee;
        color: #c00;
        padding: 10px;
        border-radius: 4px;
        margin: 10px 0;
        font-size: 12px;
        border: 1px solid #fcc;
      `;

      // Try to insert near coupon input
      const couponInput =
        document.getElementById("couponCodeInput") ||
        document.getElementById("couponCodeInputMain");
      if (couponInput && couponInput.parentNode) {
        couponInput.parentNode.insertBefore(errorEl, couponInput.nextSibling);
      }
    }

    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = "block";

      // Hide error after 5 seconds
      setTimeout(() => {
        errorEl.style.display = "none";
      }, 5000);
    } else {
      // Fallback to alert
      alert(message);
    }
  }
}

// Initialize the ApplyCoupon instance
window.applyCouponService = new ApplyCoupon();

// Check applied state on page load
document.addEventListener("DOMContentLoaded", function () {
  if (window.applyCouponService) {
    window.applyCouponService.checkAppliedState();
  }
});

// Listen for cart updates to check if coupons are removed
document.addEventListener("cart:updated", function (event) {
  if (window.applyCouponService && event.detail && event.detail.cart) {
    const cart = event.detail.cart;

    // Check if there are any active cart level discounts
    const hasActiveDiscounts =
      cart.cart_level_discount_applications &&
      cart.cart_level_discount_applications.length > 0;

    // If no active discounts but we have stored applied coupon, reset the state
    const appliedCode = sessionStorage.getItem("appliedCouponCode");
    if (!hasActiveDiscounts && appliedCode) {
      window.applyCouponService.resetApplyButtonStates();
    }
  }
});

/**
 * Global function to apply coupon (replaces existing implementations)
 * @param {string} code - The coupon code to apply
 */
async function applyCoupon(code) {
  const result = await window.applyCouponService.applyDiscount(code);
  return result;
}

/**
 * Apply coupon from coupon drawer input
 */
async function applyCouponCode() {
  const couponInput = document.getElementById("couponCodeInput");
  const errorContainer = document.getElementById(
    "coupon-drawer-error-container"
  );
  const errorDiv = document.getElementById("coupon-drawer-error");
  const errorBtn = document.getElementById("coupon-drawer-error-btn");
  const applyBtn = document.getElementById("coupon-drawer-apply-btn");
  const code = couponInput ? couponInput.value.trim() : "";

  // Clear any previous error states
  if (errorContainer) {
    errorContainer.classList.remove("show");
  }
  if (errorDiv) {
    errorDiv.textContent = "";
  }
  if (errorBtn) {
    errorBtn.classList.remove("show");
  }
  if (applyBtn) {
    applyBtn.classList.remove("hide");
  }

  const result = await applyCoupon(code);

  if (result.success && couponInput) {
    couponInput.value = "";
  } else if (!result.success) {
    // Show error message
    if (errorContainer) {
      errorContainer.classList.add("show");
    }
    if (errorDiv) {
      errorDiv.textContent = result.message || "Failed to apply coupon";
    }
    if (errorBtn) {
      errorBtn.classList.add("show");
    }
    if (applyBtn) {
      applyBtn.classList.add("hide");
    }
  }
}

/**
 * Apply coupon from main cart view
 */
async function applyCouponFromMain() {
  const couponInput = document.getElementById("couponCodeInputMain");
  const errorContainer = document.getElementById("coupon-main-error-container");
  const errorDiv = document.getElementById("coupon-main-error");
  const errorBtn = document.getElementById("coupon-main-error-btn");
  const applyBtn = document.getElementById("coupon-main-apply-btn");
  const code = couponInput ? couponInput.value.trim() : "";

  // Clear any previous error states
  if (errorContainer) {
    errorContainer.classList.remove("show");
  }
  if (errorDiv) {
    errorDiv.textContent = "";
  }
  if (errorBtn) {
    errorBtn.classList.remove("show");
  }
  if (applyBtn) {
    applyBtn.classList.remove("hide");
  }

  const result = await applyCoupon(code);

  if (result.success && couponInput) {
    couponInput.value = "";
  } else if (!result.success) {
    // Show error message
    if (errorContainer) {
      errorContainer.classList.add("show");
    }
    if (errorDiv) {
      errorDiv.textContent = result.message || "Failed to apply coupon";
    }
    if (errorBtn) {
      errorBtn.classList.add("show");
    }
    if (applyBtn) {
      applyBtn.classList.add("hide");
    }
  }
}

/**
 * Global function to remove applied coupon
 */
async function removeCoupon() {
  const result = await window.applyCouponService.removeDiscount();
  return result;
}

// Make functions available globally (replaces existing implementations)
window.applyCoupon = applyCoupon;
window.applyCouponCode = applyCouponCode;
window.applyCouponFromMain = applyCouponFromMain;
window.removeCoupon = removeCoupon;

// Auto-apply coupon from URL parameters on page load
document.addEventListener("DOMContentLoaded", function () {
  const urlParams = new URLSearchParams(window.location.search);
  const discountParam = urlParams.get("discount");

  if (discountParam) {
    // Auto-apply discount from URL after a short delay
    setTimeout(() => {
      applyCoupon(discountParam);
    }, 1000);
  }
});

